import { useCallback, useEffect, useRef, useState } from "react";

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export const useCamera = (active) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [live, setLive] = useState(false);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setLive(false);
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "user",
            // portrait-ish stream so the tall oval crops far less (no "zoomed in" face)
            aspectRatio: { ideal: 0.65 },
            width: { ideal: 720, max: 1080 },
            height: { ideal: 1280, max: 1920 },
            frameRate: { ideal: 30, min: 20 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setLive(true);
      } catch {
        setError("cameraDenied");
      }
    })();
    return () => {
      cancelled = true;
      stop();
    };
  }, [active, stop]);

  const grabFrames = useCallback(async (count = 3) => {
    const video = videoRef.current;
    if (!video) throw new Error("cameraDenied");
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext("2d");
    // preview is mirrored for a natural selfie feel — flip back so the stored frame is true
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    const frames = [];
    for (let i = 0; i < count; i += 1) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      frames.push(canvas.toDataURL("image/jpeg", 0.92));
      await wait(320);
    }
    return frames;
  }, []);

  return { videoRef, error, live, grabFrames, stop };
};
